import React, { forwardRef, type ReactNode, type Ref } from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Badge from "@ui/Badge";
import Tab from "@ui/NavV2/Tab";
import Text from "@ui/Text";
import t from "../../../../../i18n";
import { SUMMARY_ID } from "../utils";
import { toPath } from "../../../../../router/helpers/path";
import { useSettings } from "../../../../../api/useSettings";

interface Props {
  projectId: string;
  dashboardName: string;
  dashboardId: string;
  isActive: boolean;
  contextualMenu?: ReactNode;
}

const DashboardTab = (
  { projectId, dashboardName, dashboardId, isActive, contextualMenu }: Props,
  forwardedRef: Ref<HTMLDivElement>,
) => {
  const settings = useSettings();
  const href =
    dashboardId === SUMMARY_ID
      ? toPath("/projects/:projectId/summary/summary", { projectId })
      : toPath("/projects/:projectId/summary/dashboard/:dashboardId", { projectId, dashboardId });
  const showLegacyBadge = dashboardId === SUMMARY_ID && settings.SHOW_LEGACY_SUMMARY_BADGE;

  return (
    <div ref={forwardedRef} className={dashboardTab}>
      <Tab className={styledTab} data-section-id={dashboardId} to={href} active={isActive}>
        <div className={tabWrapper}>
          <div className={title}>
            <Text title={dashboardName}>
              {dashboardId === SUMMARY_ID ? t("project.document.title.summary") : dashboardName}
            </Text>
            {showLegacyBadge ? (
              <Badge size="s" backgroundColor={colors.carbon100} color={colors.carbon800}>
                {t("project.document.title.summary.legacy_badge")}
              </Badge>
            ) : null}
          </div>
        </div>
      </Tab>
      {isActive ? <div className={contextualMenuWrapper}>{contextualMenu}</div> : null}
    </div>
  );
};

export default forwardRef(DashboardTab);

//#region CSS
const styledTab = css`
  padding: 6px 16px;
`;

const dashboardTab = css`
  display: flex;
  align-items: center;
`;

const contextualMenuWrapper = css`
  display: flex;
  align-items: center;
  margin-left: calc(-1 * ${spacings.xs});
  padding-right: ${spacings.xs};
`;

const tabWrapper = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  max-width: 150px;
`;

const title = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};

  overflow: auto;

  & > :nth-child(1) {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
//#endregion CSS
